import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { catalogService } from '@/services';
import { useCartStore, useAuthStore, useSettingsStore } from '@/stores';
import { formatPrice } from '@/utils/format';
import { Badge, Button, PriceBlock } from '@/components/ui';
import { CatalogImage } from '../components/CatalogImage';
import { catalogStockCountLabel, compactStockCount } from '../utils/stockDisplay';

function normalizeDetailKey(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

function formatUsdSecondary(value?: number | null): string | undefined {
  if (value == null) return undefined;
  return `USD ${formatPrice(value, 'USD').replace(/^USD\s+/, '')}`;
}

function formatCopPrimary(
  price?: { price: number; currency: string; priceCop?: number; priceUsd?: number } | null,
): { amount: string; secondary?: string } | null {
  if (!price) return null;
  const currency = price.currency?.toUpperCase();
  const cop = currency === 'COP' ? price.price : price.priceCop;
  const usd = currency === 'USD' ? price.price : price.priceUsd;

  return {
    amount: cop != null ? formatPrice(cop, 'COP') : formatPrice(price.price, price.currency),
    secondary: formatUsdSecondary(usd),
  };
}

export function TcgCardDetailPage(): JSX.Element {
  const { t } = useTranslation('catalog');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { addTcg, isLoading: isCartLoading } = useCartStore();
  const currency = useSettingsStore((s) => s.currency);
  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [addedToCartMessage, setAddedToCartMessage] = useState(false);
  const [previewOfferImage, setPreviewOfferImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);

  // Get printing identifier from query params
  const setCode = searchParams.get('setCode');
  const setRarityCode = searchParams.get('setRarityCode');
  const setEdition = searchParams.get('setEdition');

  // setCode identifies the printing. setEdition helps local matching when available,
  // but external catalog suggestions may not include it.
  const hasValidPrintingParams = !!setCode;

  const { data: printing, isLoading: isPrintingLoading } = useQuery({
    queryKey: ['tcg-printing', id, setCode, setRarityCode, setEdition, currency],
    queryFn: async () => {
      if (!id) throw new Error('Card ID is required');
      if (!hasValidPrintingParams) {
        throw new Error('Missing required printing identifier parameter (setCode)');
      }

      if (setEdition) {
        try {
          return await catalogService.getPrintingById({
            cardId: id,
            setCode: setCode!,
            setRarityCode: setRarityCode || null,
            setEdition,
            currency,
          });
        } catch {
          // Fall back to the external catalog so cards without local products
          // can still render as informative results.
        }
      }

      return catalogService.getExternalPrintingById({
        cardId: id,
        setCode: setCode!,
        setRarityCode: setRarityCode || null,
        setEdition: setEdition || null,
        currency,
      });
    },
    enabled: !!id && hasValidPrintingParams,
  });

  const listingsFromPrinting = useMemo(() => printing?.listings ?? [], [printing?.listings]);
  const fallbackCardId = printing?.card.id ?? id;

  // Fallback: when backend says there are listings but doesn't return the array, fetch by master card and filter by printing
  const { data: fallbackListings = [] } = useQuery({
    queryKey: ['tcg-listings-fallback', fallbackCardId, setCode, setRarityCode, setEdition],
    queryFn: () => catalogService.getActiveListingsByMasterCard(fallbackCardId!),
    enabled: !!fallbackCardId && !!printing && (printing.aggregatedStock?.listingCount ?? 0) > 0 && listingsFromPrinting.length === 0,
  });

  const listings = useMemo(() => {
    if (listingsFromPrinting.length > 0) return listingsFromPrinting;
    const code = normalizeDetailKey(setCode);
    const rarity = normalizeDetailKey(setRarityCode);
    const edition = normalizeDetailKey(setEdition);
    return fallbackListings.filter((l: { setCode?: string | null; setRarityCode?: string | null; setEdition?: string | null }) => {
      const lCode = normalizeDetailKey(l.setCode);
      const lRarity = normalizeDetailKey(l.setRarityCode);
      const lEdition = normalizeDetailKey(l.setEdition);
      return lCode === code && lRarity === rarity && lEdition === edition;
    });
  }, [listingsFromPrinting, fallbackListings, setCode, setRarityCode, setEdition]);

  // Reset and validate quantity when selected listing changes
  useEffect(() => {
    if (selectedListingId) {
      const selectedListing = listings.find(l => l.id === selectedListingId);
      if (selectedListing) {
        // Reset quantity to available stock (or 1 if stock is 0) when listing changes
        // This ensures quantity never exceeds the new listing's stock
        setQuantity(prevQuantity => {
          const maxStock = selectedListing.stock;
          if (prevQuantity > maxStock) {
            return Math.max(1, maxStock);
          }
          return prevQuantity;
        });
      }
    } else {
      // Reset to 1 when no listing is selected
      setQuantity(1);
    }
  }, [selectedListingId, listings]);

  const handleAddToCart = async () => {
    if (!selectedListingId || !id) {
      setError(tc('common.pleaseSelectListing'));
      return;
    }

    if (!isAuthenticated) {
      const currentUrl = `/yugioh/cards/${id}?setCode=${encodeURIComponent(setCode || '')}&setRarityCode=${encodeURIComponent(setRarityCode || '')}&setEdition=${encodeURIComponent(setEdition || '')}`;
      navigate('/login', { state: { from: currentUrl } });
      return;
    }

    setError(null);
    try {
      await addTcg(id, selectedListingId, quantity);
      setAddedToCartMessage(true);
      setTimeout(() => setAddedToCartMessage(false), 2500);
    } catch (error) {
      const message = error instanceof Error ? error.message : tc('common.error');
      setError(message);
    }
  };

  // Handle missing printing identifier params
  if (!hasValidPrintingParams) {
    return (
      <div className="mx-auto flex min-h-[400px] max-w-7xl items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
        <div className="font-sans text-lg text-red-600">
          {tc('common.missingPrintingParams')}
        </div>
      </div>
    );
  }

  if (isPrintingLoading) {
    return (
      <div className="mx-auto flex min-h-[400px] max-w-7xl items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
        <div className="font-sans text-lg text-brand-700">{tc('common.loadingPrintingDetails')}</div>
      </div>
    );
  }

  if (!printing) {
    return (
      <div className="mx-auto flex min-h-[400px] max-w-7xl items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
        <div className="font-sans text-lg text-red-600">{tc('common.printingNotFound')}</div>
      </div>
    );
  }

  const card = printing.card;
  const hasListings = listings.length > 0;
  const listingForFeaturedImage = selectedListingId
    ? listings.find((listing) => listing.id === selectedListingId) ??
      listings.find((listing) => Boolean(listing.product?.images?.[0]?.url))
    : listings.find((listing) => Boolean(listing.product?.images?.[0]?.url));
  const featuredProductImage = listingForFeaturedImage?.product?.images?.[0] ?? null;
  const featuredListingImageUrl = featuredProductImage?.url ?? null;
  const featuredListingImageAlt =
    featuredProductImage?.alt ?? listingForFeaturedImage?.product?.name ?? card.name;
  const availableStock =
    printing.aggregatedStock?.aggregatedStock ??
    listings.reduce((total, listing) => total + (listing.stock ?? 0), 0);
  const isInStock = printing.availability.isAvailable && availableStock > 0;
  const statistics = [
    card.atk !== null && card.atk !== undefined ? ['ATK', card.atk] : null,
    card.def !== null && card.def !== undefined ? ['DEF', card.def] : null,
    card.level !== null && card.level !== undefined ? ['Level', card.level] : null,
    card.rank !== null && card.rank !== undefined ? ['Rank', card.rank] : null,
  ].filter(Boolean) as Array<[string, number]>;
  const attributes = [
    card.attribute ? ['Attribute', card.attribute] : null,
    card.race ? ['Race', card.race] : null,
    card.archetype ? ['Archetype', card.archetype] : null,
  ].filter(Boolean) as Array<[string, string]>;
  const advancedStats = [
    card.scale !== null && card.scale !== undefined ? ['Scale', card.scale] : null,
    card.linkval !== null && card.linkval !== undefined ? ['Link Value', card.linkval] : null,
  ].filter(Boolean) as Array<[string, number]>;
  const fromPriceDisplay = formatCopPrimary(printing.fromPrice);
  const setPriceUsd = printing.setPriceUsd ?? (
    printing.setPriceCurrency === 'USD' ? printing.setPrice : null
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {addedToCartMessage && (
        <div
          className="fixed right-4 top-4 z-50 rounded-lg bg-green-600 px-4 py-2 text-white shadow-lg"
          role="status"
        >
          {tc('common.addedToCart')}
        </div>
      )}
      {previewOfferImage ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-950/75 px-4 py-6"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewOfferImage(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-3xl overflow-hidden rounded-lg bg-surface p-3 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-brand-950/85 font-sans text-sm font-semibold text-white transition-colors hover:bg-brand-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              onClick={() => setPreviewOfferImage(null)}
              aria-label="Cerrar imagen"
            >
              x
            </button>
            <CatalogImage
              src={previewOfferImage.src}
              alt={previewOfferImage.alt}
              className="max-h-[82vh] w-full rounded-md object-contain"
            />
          </div>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 font-sans font-medium text-brand-600 hover:text-brand-700 hover:underline"
      >
        ← {tc('common.back')}
      </button>

      <div className="mb-6 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(240px,340px)_minmax(0,1fr)]">
        <div className="space-y-3">
          <div className="overflow-hidden rounded-lg border border-brand-700/15 bg-surface shadow-sm">
            <div className="aspect-[2/3] max-h-[520px] w-full bg-brand-100">
              <button
                type="button"
                className="h-full w-full disabled:cursor-default"
                disabled={!featuredListingImageUrl}
                onClick={() => {
                  if (!featuredListingImageUrl) return;
                  setPreviewOfferImage({
                    src: featuredListingImageUrl,
                    alt: featuredListingImageAlt,
                  });
                }}
                aria-label={`Ampliar imagen de ${featuredListingImageAlt}`}
              >
                <CatalogImage
                  src={featuredListingImageUrl}
                  alt={featuredListingImageAlt}
                  className="h-full w-full object-contain"
                />
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-brand-700/15 bg-surface p-4 shadow-sm sm:p-5">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="font-heading text-heading-lg font-bold text-brand-950 sm:text-heading-xl">
                {card.name}
              </h1>
              {card.type ? (
                <p className="mt-1 font-sans text-sm text-brand-700">
                  <span className="font-semibold">{t('detail.cardTypeLabel')}</span> {card.type}
                </p>
              ) : null}
            </div>
            <Badge variant={isInStock ? 'success' : 'danger'}>
              {isInStock ? catalogStockCountLabel(t, availableStock) : t('stock.outOfStock')}
            </Badge>
          </div>

          {(printing.setCode || printing.setName || printing.setRarity || printing.setRarityCode || printing.setEdition) && (
            <div className="mb-4 rounded-lg bg-brand-100 p-3">
              <h2 className="mb-2 font-heading text-heading-sm font-semibold text-brand-950">
                {tc('common.printingInfo')}
              </h2>
              <div className="grid gap-x-4 gap-y-1 font-sans text-xs text-brand-950 sm:grid-cols-2">
                {printing.setName ? (
                  <div><span className="font-semibold">Set:</span> {printing.setName}</div>
                ) : null}
                {printing.setCode ? (
                  <div><span className="font-semibold">Code:</span> {printing.setCode}</div>
                ) : null}
                {printing.setRarity ? (
                  <div><span className="font-semibold">Rarity:</span> {printing.setRarity}</div>
                ) : null}
                {printing.setRarityCode ? (
                  <div><span className="font-semibold">Rarity Code:</span> {printing.setRarityCode}</div>
                ) : null}
                {printing.setEdition ? (
                  <div><span className="font-semibold">Edition:</span> {printing.setEdition}</div>
                ) : null}
              </div>
            </div>
          )}

          {card.description ? (
            <p className="mb-4 max-w-3xl font-sans text-sm leading-6 text-brand-950">
              {card.description}
            </p>
          ) : null}

          {statistics.length > 0 || attributes.length > 0 || advancedStats.length > 0 ? (
            <div className="mb-4 grid gap-3 font-sans text-sm text-brand-950 md:grid-cols-3">
              {statistics.length > 0 ? (
                <div>
                  <h3 className="mb-1 font-heading text-heading-xs font-semibold text-brand-950">
                    {tc('common.statistics')}
                  </h3>
                  <div className="space-y-1">
                    {statistics.map(([label, value]) => (
                      <div key={label}><span className="font-semibold">{label}:</span> {value}</div>
                    ))}
                  </div>
                </div>
              ) : null}
              {attributes.length > 0 ? (
                <div>
                  <h3 className="mb-1 font-heading text-heading-xs font-semibold text-brand-950">
                    {tc('common.attributes')}
                  </h3>
                  <div className="space-y-1">
                    {attributes.map(([label, value]) => (
                      <div key={label}><span className="font-semibold">{label}:</span> {value}</div>
                    ))}
                  </div>
                </div>
              ) : null}
              {advancedStats.length > 0 ? (
                <div>
                  <h3 className="mb-1 font-heading text-heading-xs font-semibold text-brand-950">
                    {tc('common.advancedStats')}
                  </h3>
                  <div className="space-y-1">
                    {advancedStats.map(([label, value]) => (
                      <div key={label}><span className="font-semibold">{label}:</span> {value}</div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap items-end gap-4">
            {printing.setPrice != null ? (
              <div className="font-sans">
                <span className="block text-xs text-brand-700">{tc('common.tcgCardValue')}</span>
                <PriceBlock
                  amount={formatPrice(printing.setPrice, printing.setPriceCurrency || 'COP')}
                  secondary={formatUsdSecondary(setPriceUsd)}
                  size="sm"
                />
              </div>
            ) : null}

            {fromPriceDisplay ? (
              <PriceBlock
                amount={`${t('price.from')} ${fromPriceDisplay.amount}`}
                secondary={fromPriceDisplay.secondary}
                size="md"
              />
            ) : null}

            {printing.aggregatedStock ? (
              <div className="font-sans text-xs text-brand-700">
                <div>{t('labels.availableStock')}: {printing.aggregatedStock.aggregatedStock}</div>
                <div>{t('labels.listings')}: {printing.aggregatedStock.listingCount}</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Listings Selection */}
      <div className="rounded-lg border border-brand-700/15 bg-surface p-4 shadow-sm sm:p-5">
        <h2 className="mb-3 font-heading text-heading-md font-bold text-brand-950">{t('offers.title')}</h2>

        {!hasListings ? (
          <div className="py-6 text-center font-sans text-sm text-brand-700">
            {printing.aggregatedStock && printing.aggregatedStock.listingCount > 0 ? (
              <div>
                <p>
                  {t('offers.countInfo', { count: printing.aggregatedStock.listingCount })}
                </p>
                <p className="mt-1 text-xs text-brand-700/70">{t('offers.countNote')}</p>
              </div>
            ) : (
              <p>{t('offers.none')}</p>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4 space-y-2">
              {listings.map((listing) => {
                const isSelected = selectedListingId === listing.id;
                const productImage = listing.product?.images?.[0] ?? null;
                const listingImageUrl = productImage?.url ?? null;
                const listingImageAlt = productImage?.alt ?? listing.product?.name ?? card.name;
                const hasProductImage = Boolean(listingImageUrl);
                const listingPriceDisplay = formatCopPrimary(listing);
                const listingStockLabel = `${compactStockCount(listing.stock)} ${tc('common.available')}`;
                const listingSecondary = listingPriceDisplay?.secondary
                  ? `${listingPriceDisplay.secondary} / ${listingStockLabel}`
                  : listingStockLabel;
                // Determine seller label from seller role (ADMINISTRATOR = ADMIN, SELLER or others = SELLER)
                const isAdminSeller = listing.seller?.role === 'ADMINISTRATOR';
                const sellerLabel = isAdminSeller ? t('seller.admin') : t('seller.seller');

                return (
                  <div
                    key={listing.id}
                    className={`cursor-pointer rounded-lg border-2 p-3 transition-all ${
                      isSelected
                        ? 'border-brand-400 bg-brand-100'
                        : 'border-brand-700/20 hover:border-brand-700/40'
                    }`}
                    onClick={() => setSelectedListingId(listing.id)}
                  >
                    <div className="grid gap-3 sm:grid-cols-[78px_minmax(0,1fr)_auto]">
                      <button
                        type="button"
                        className="h-24 w-[78px] flex-shrink-0 overflow-hidden rounded bg-brand-100 transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-default disabled:hover:shadow-none"
                        disabled={!hasProductImage}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!listingImageUrl) return;
                          setPreviewOfferImage({
                            src: listingImageUrl,
                            alt: listingImageAlt,
                          });
                        }}
                        aria-label={`Ampliar imagen de ${listingImageAlt}`}
                      >
                        <CatalogImage
                          src={listingImageUrl}
                          alt={listingImageAlt}
                          className="h-full w-full object-contain"
                        />
                      </button>
                      <div className="min-w-0">
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="line-clamp-1 font-heading text-sm font-semibold text-brand-950">
                              {listing.product?.name ?? card.name}
                            </h3>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5 font-sans text-xs text-brand-700">
                              <Badge
                                variant={isAdminSeller ? 'brand' : 'neutral'}
                              >
                                {sellerLabel}
                              </Badge>
                              {listing.seller?.displayName && (
                                <span>{tc('common.soldBy')}: {listing.seller.displayName}</span>
                              )}
                            </div>
                          </div>
                          <div className="hidden text-right sm:block">
                            <PriceBlock
                              amount={
                                listingPriceDisplay?.amount ??
                                (typeof listing.price === 'number'
                                  ? formatPrice(listing.price, listing.currency)
                                  : `${listing.currency} ${String(listing.price)}`)
                              }
                              size="md"
                              secondary={listingSecondary}
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 text-xs">
                          <Badge variant="neutral">
                            {tc('common.condition')}: {listing.condition}
                          </Badge>
                          {listing.language && (
                            <Badge variant="neutral">
                              {tc('common.language')}: {listing.language}
                            </Badge>
                          )}
                          {listing.printingVariant && (
                            <Badge variant="neutral">
                              {listing.printingVariant}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 sm:justify-end">
                        <div className="text-left sm:hidden">
                          <PriceBlock
                            amount={
                              listingPriceDisplay?.amount ??
                              (typeof listing.price === 'number'
                                ? formatPrice(listing.price, listing.currency)
                                : `${listing.currency} ${String(listing.price)}`)
                            }
                            size="sm"
                            secondary={listingSecondary}
                          />
                        </div>
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => setSelectedListingId(listing.id)}
                          className="h-4 w-4 text-brand-600"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {error && (
              <div className="mb-4 rounded bg-red-100 p-3 font-sans text-red-800">
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label htmlFor="quantity" className="font-sans text-sm font-semibold text-brand-950">
                  {tc('common.quantity')}:
                </label>
                <input
                  id="quantity"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (val > 0 && selectedListingId) {
                      const selectedListing = listings.find(l => l.id === selectedListingId);
                      if (selectedListing && val <= selectedListing.stock) {
                        setQuantity(val);
                      }
                    }
                  }}
                  className="w-[4.5rem] rounded border border-brand-700/35 bg-surface px-2.5 py-1.5 font-sans text-sm text-brand-950"
                  disabled={!selectedListingId}
                />
                {selectedListingId && (
                  <span className="font-sans text-xs text-brand-700">
                    Max: {compactStockCount(listings.find(l => l.id === selectedListingId)?.stock || 0)}
                  </span>
                )}
              </div>
              <Button
                onClick={handleAddToCart}
                disabled={!selectedListingId || isCartLoading || !hasListings}
                loading={isCartLoading}
              >
                {isCartLoading ? tc('common.addingToCart') : tc('common.addToCart')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
